import React from 'react';
import { Shield, Cloud, FileCheck, Bell, RefreshCw, Users, Brain, Siren, Database, GraduationCap, ArrowRight } from 'lucide-react';

interface ServicesProps {
  setCurrentPage?: (page: string) => void;
}

const Services: React.FC<ServicesProps> = ({ setCurrentPage }) => {
  const services = [
    {
      icon: Shield,
      title: 'Managed Security',
      description: '24/7 security monitoring and management of your infrastructure',
      link: 'managed-security'
    },
    {
      icon: Bell,
      title: 'Security Operations Center (SOC)',
      description: 'Dedicated SOC team for continuous threat monitoring and response',
      link: 'soc'
    },
    {
      icon: Cloud,
      title: 'Cloud Security and Infrastructure',
      description: 'Secure cloud infrastructure and applications with comprehensive protection',
      link: 'cloud-security'
    },
    {
      icon: FileCheck,
      title: 'Compliance and Risk Management',
      description: 'Meet regulatory requirements and manage security risks effectively',
      link: 'compliance'
    },
    {
      icon: RefreshCw,
      title: 'Transformation and Process Optimization',
      description: 'Streamline and enhance security processes for maximum efficiency',
      link: 'transformation'
    },
    {
      icon: Users,
      title: 'Specialized Industry',
      description: 'Custom security solutions tailored to your industry needs',
      link: 'specialized-industry'
    },
    {
      icon: Brain,
      title: 'Threat Intelligence and Awareness',
      description: 'Advanced threat detection and prevention with AI-powered insights',
      link: 'threat-intelligence'
    },
    {
      icon: Siren,
      title: 'Incident Response and Recovery',
      description: 'Swift response to security incidents with minimal business impact',
      link: 'incident-response'
    },
    {
      icon: Database,
      title: 'Data Security',
      description: 'Comprehensive data protection and breach prevention strategies',
      link: 'data-security'
    },
    {
      icon: GraduationCap,
      title: 'Training & Awareness',
      description: 'Security education and awareness programs for your team',
      link: 'training'
    }
  ];

  return (
    <section className="py-32 bg-white dark:bg-gray-900 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0">
        <div className="absolute w-96 h-96 -left-48 top-0 bg-blue-500/5 rounded-full blur-3xl"></div>
        <div className="absolute w-96 h-96 -right-48 bottom-0 bg-purple-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="gradient-text">Comprehensive Security Services</span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Protect your business with our full range of cybersecurity services
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 auto-rows-fr">
          {services.map((service, index) => (
            <div
              key={index}
              className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-8 hover-card flex flex-col"
            >
              <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl mb-6 flex-none">
                <service.icon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 flex-none">
                {service.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-6 flex-grow">
                {service.description}
              </p>
              {setCurrentPage && service.link && (
                <button
                  onClick={() => {
                    setCurrentPage(service.link);
                  }}
                  className="w-full py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:opacity-90 transition-all duration-300 flex items-center justify-center flex-none"
                >
                  Learn More
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;